<template>
  <div class="pa-4">
    <v-app-bar flat color="transparent" class="mb-2">
      <v-toolbar-title class="title">Ajuste de Numeração da NF-e</v-toolbar-title>
      <v-spacer />
      <v-btn text color="primary" :loading="lendo" @click="carregarNumeracao">
        <v-icon left small>mdi-refresh</v-icon> Reconsultar
      </v-btn>
    </v-app-bar>

    <v-alert type="warning" outlined dense class="mb-4">
      O número informado será o número da <b>próxima nota</b> emitida. Só é permitido <b>aumentar</b> a numeração.
      A emissão de notas não é bloqueada durante o ajuste; confirme o resultado na consulta exibida após alterar.
    </v-alert>

    <v-card outlined class="mb-4">
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field v-model="cnpj" label="CNPJ da empresa emitente" outlined dense hide-details="auto" @change="carregarNumeracao" />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.number="serie" label="Série" type="number" outlined dense hide-details="auto" @change="carregarNumeracao" />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field :value="leitura.ok ? leitura.proximo : '-'" label="Próximo número no PlugNotas (atual)" outlined dense readonly hide-details="auto" />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field :value="ambiente" label="Ambiente" outlined dense readonly hide-details="auto" />
          </v-col>
        </v-row>

        <v-alert v-if="leitura.erro" type="error" dense outlined class="mt-3 mb-0">{{ leitura.erro }}</v-alert>

        <v-row dense class="mt-2">
          <v-col cols="12" md="3">
            <v-text-field
              v-model="numeroNovo"
              label="Novo número (próxima nota)"
              type="number"
              outlined
              dense
              :error-messages="erroNumero"
              :disabled="!leitura.ok"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="motivo" label="Motivo (opcional)" outlined dense :disabled="!leitura.ok" />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn color="primary" block :disabled="!podeAjustar" :loading="ajustando" @click="confirmarDialog = true">
              Ajustar numeração
            </v-btn>
          </v-col>
        </v-row>

        <v-alert v-if="resultado" :type="resultado.ok ? 'success' : 'error'" dense outlined class="mt-2 mb-0">
          <template v-if="resultado.ok">
            Numeração alterada e confirmada no PlugNotas: a próxima nota sairá com o número <b>{{ resultado.numeroConfirmado }}</b>.
          </template>
          <template v-else>
            {{ resultado.erro }}
          </template>
        </v-alert>
      </v-card-text>
    </v-card>

    <v-card outlined>
      <v-card-title class="subtitle-1">Histórico de ajustes</v-card-title>
      <v-card-text>
        <v-data-table :headers="headers" :items="historico" :loading="carregandoHistorico" no-data-text="Nenhum ajuste registrado." dense>
          <template v-slot:item.dataSolicitacao="{ item }">
            {{ item.dataSolicitacao ? new Date(item.dataSolicitacao).toLocaleString('pt-BR') : '-' }}
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="corStatus(item.status)" dark>{{ item.status }}</v-chip>
          </template>
          <template v-slot:item.retornoApi="{ item }">
            <span :title="item.retornoApi">{{ (item.retornoApi || '').slice(0, 60) }}{{ (item.retornoApi || '').length > 60 ? '…' : '' }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn v-if="item.status !== 'Sucesso'" small text color="primary" :loading="ajustando" @click="tentarNovamente(item)">
              Tentar novamente
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="confirmarDialog" max-width="480">
      <v-card>
        <v-card-title class="subtitle-1">Confirmar ajuste de numeração</v-card-title>
        <v-card-text>
          A próxima NF-e (série {{ serie }}) passará do número <b>{{ leitura.proximo }}</b> para <b>{{ numeroNovo }}</b>.
          <span v-if="leitura.producao"><br /><b>Empresa em PRODUÇÃO.</b> Números pulados precisam ser inutilizados na SEFAZ.</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmarDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="executarAjuste">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { CNPJ_PADRAO, SERIE_PADRAO, lerNumeracao, validarNovoNumero, ajustarNumeracao, listarHistorico } from "@/api/v2/services/AjusteNumeracaoNfeService";

export default {
  name: "AjusteNumeracaoNfeView",
  data() {
    return {
      cnpj: CNPJ_PADRAO,
      serie: SERIE_PADRAO,
      leitura: { ok: false },
      lendo: false,
      numeroNovo: "",
      motivo: "",
      ajustando: false,
      confirmarDialog: false,
      resultado: null,
      historico: [],
      carregandoHistorico: false,
      headers: [
        { text: "Data/Hora", value: "dataSolicitacao" },
        { text: "Usuário", value: "usuario" },
        { text: "Série", value: "serie" },
        { text: "Anterior", value: "numeroAnterior" },
        { text: "Novo", value: "numeroNovo" },
        { text: "Confirmado", value: "numeroConfirmado" },
        { text: "Status", value: "status" },
        { text: "Retorno", value: "retornoApi", sortable: false },
        { text: "Tentativas", value: "tentativas" },
        { text: "", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    ambiente() {
      if (!this.leitura.ok) return "-";
      return this.leitura.producao ? "Produção" : "Homologação";
    },
    erroNumero() {
      if (this.numeroNovo === "" || !this.leitura.ok) return [];
      const e = validarNovoNumero(this.numeroNovo, this.leitura);
      return e ? [e] : [];
    },
    podeAjustar() {
      return this.leitura.ok && this.numeroNovo !== "" && !this.erroNumero.length && !this.ajustando;
    },
  },
  created() {
    this.carregarNumeracao();
    this.carregarHistorico();
  },
  methods: {
    async carregarNumeracao() {
      this.lendo = true;
      this.leitura = await lerNumeracao(this.cnpj, this.serie);
      this.lendo = false;
    },
    async carregarHistorico() {
      this.carregandoHistorico = true;
      this.historico = await listarHistorico();
      this.carregandoHistorico = false;
    },
    corStatus(s) {
      return s === "Sucesso" ? "success" : s === "Erro" ? "error" : "orange";
    },
    async executarAjuste() {
      this.confirmarDialog = false;
      this.ajustando = true;
      this.resultado = await ajustarNumeracao({ cnpj: this.cnpj, serie: this.serie, numeroNovo: this.numeroNovo, motivo: this.motivo });
      this.ajustando = false;
      if (this.resultado.ok) {
        this.numeroNovo = "";
        this.motivo = "";
      }
      await this.carregarNumeracao();
      await this.carregarHistorico();
    },
    async tentarNovamente(item) {
      this.ajustando = true;
      this.resultado = await ajustarNumeracao({
        cnpj: item.empresaCnpj || this.cnpj,
        serie: item.serie || this.serie,
        numeroNovo: item.numeroNovo,
        motivo: item.motivo,
        logExistente: item,
      });
      this.ajustando = false;
      await this.carregarNumeracao();
      await this.carregarHistorico();
    },
  },
};
</script>
